import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { createPost, updatePost, getPost, slugify } from "../../utils/posts";
import { uploadFile } from "../../utils/storage";
import { useAuth } from "../../context/AuthContext";
import toast from "react-hot-toast";

// Shared formatter component + exported heading lists from the public page
// (keeps PostDetailPage as the single source of truth for all heading data)
import { FormattedReportContent, MACRO_NOTE_HEADINGS } from "../public/PostDetailPage";

const CATEGORIES = [
  { value: "macro-note",     label: "Macro Note" },
  { value: "weekly-outlook", label: "Weekly Outlook" },
  { value: "trade-idea",     label: "Trade Idea" },
  { value: "education",      label: "Education" },
  { value: "resource",       label: "Resource" },
];

const CATEGORY_COLORS = {
  "weekly-outlook": "#f5c518",
  "trade-idea":     "#4ade80",
  "macro-note":     "#818cf8",
  "education":      "#f87171",
  "resource":       "#c9a84c",
};

// ─────────────────────────────────────────────────────────────────────────────
// Per-category content configuration
//   headings   — shown in the cheat-sheet pill list
//   template   — inserted by the "Insert Template" button
//   placeholder — shown inside the textarea when empty
// ─────────────────────────────────────────────────────────────────────────────
const WEEKLY_OUTLOOK_HEADINGS = [
  "Market Overview",
  "Central Banks This Week",
  "Oil and Energy Markets",
  "Gold and Safe Haven Assets",
  "Currencies and Dollar Dynamics",
  "Equities and Risk Sentiment",
  "Geopolitical Risk Overlay",
  "Economic Data to Watch",
  "Market Bias and Key Levels",
  "Risks to the View",
];

const CATEGORY_CONTENT_CONFIG = {
  "macro-note": {
    headings: MACRO_NOTE_HEADINGS,
    template: MACRO_NOTE_HEADINGS.map(h => `${h}\n\n`).join("\n"),
    placeholder:
      "Paste your Macro Note here. No HTML needed.\n\nUse the section headings exactly as shown below and separate paragraphs with a blank line.\n\nExample:\n\nGlobal Macro Environment\nWrite your macro overview here...\n\nTrade and Tariff Dynamics\nWrite your tariff analysis here...",
  },
  "weekly-outlook": {
    headings: WEEKLY_OUTLOOK_HEADINGS,
    template: WEEKLY_OUTLOOK_HEADINGS.map(h => `${h}\n\n`).join("\n"),
    placeholder:
      "Paste your Weekly Outlook here. No HTML needed.\n\nUse the section headings exactly as shown below and separate paragraphs with a blank line.\n\nExample:\n\nMarket Overview\nWrite your market overview here...\n\nCentral Banks This Week\nWrite your central bank analysis here...",
  },
};

// Fallback config for categories that have no fixed structure
const DEFAULT_CONTENT_CONFIG = {
  headings: [],
  template: null,
  placeholder:
    "Write your full content here.\n\nSeparate paragraphs with a blank line. HTML tags are also supported if needed.",
};

const EMPTY = {
  title: "",
  slug: "",
  excerpt: "",
  content: "",
  category: "macro-note",
  tags: "",
  author: "MacroEdge Research",
  coverImage: "",
  featured: false,
  status: "draft",
};

export default function AdminPostEditor() {
  const { id } = useParams();
  const isEditing = Boolean(id);
  const navigate = useNavigate();
  const { user } = useAuth();

  const [form, setForm]               = useState(EMPTY);
  const [loading, setLoading]         = useState(isEditing);
  const [saving, setSaving]           = useState(false);
  const [imageUploading, setImageUploading] = useState(0);
  const [showPreview, setShowPreview] = useState(false);
  const fileRef = useRef();

  useEffect(() => {
    if (!isEditing) return;
    getPost(id)
      .then(post => {
        if (!post) { toast.error("Post not found."); navigate("/admin/posts"); return; }
        setForm({
          ...post,
          tags: (post.tags || []).join(", "),
          publishDate: post.publishDate?.toDate?.()?.toISOString?.()?.substring(0, 10) || "",
        });
      })
      .catch(() => toast.error("Failed to load post."))
      .finally(() => setLoading(false));
  }, [id, isEditing, navigate]);

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const handleTitleChange = (e) => {
    const title = e.target.value;
    set("title", title);
    if (!isEditing || !form.slug) set("slug", slugify(title));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const { url } = await uploadFile(file, "covers", (pct) => setImageUploading(pct));
      set("coverImage", url);
      toast.success("Image uploaded.");
    } catch {
      toast.error("Image upload failed.");
    } finally {
      setImageUploading(0);
    }
  };

  const handleSave = async (status) => {
    if (!form.title.trim()) return toast.error("Title is required.");
    setSaving(true);
    const data = {
      title:       form.title.trim(),
      slug:        form.slug || slugify(form.title),
      excerpt:     form.excerpt.trim(),
      content:     form.content,           // saved as-is; formatting happens on render
      category:    form.category,
      tags:        form.tags.split(",").map(t => t.trim()).filter(Boolean),
      author:      form.author || user?.email || "MacroEdge Research",
      coverImage:  form.coverImage || "",
      featured:    form.featured,
      status:      status || form.status,
      publishDate: form.publishDate ? new Date(form.publishDate) : new Date(),
    };
    try {
      if (isEditing) {
        await updatePost(id, data);
        toast.success("Post updated.");
      } else {
        await createPost(data);
        toast.success("Post created.");
        navigate("/admin/posts");
      }
    } catch (err) {
      toast.error("Save failed: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 300 }}>
      <div className="spinner" />
    </div>
  );

  const accentColor   = CATEGORY_COLORS[form.category] || "#c9a84c";
  const contentConfig = CATEGORY_CONTENT_CONFIG[form.category] || DEFAULT_CONTENT_CONFIG;

  return (
    <div>
      {/* ── Header ── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 32, flexWrap: "wrap", gap: 16 }}>
        <div>
          <div style={{ fontFamily: "var(--font-mono)", fontSize: "10px", letterSpacing: "0.2em", textTransform: "uppercase", color: "var(--gold)", marginBottom: 6 }}>
            {isEditing ? "Edit Post" : "New Post"}
          </div>
          <h2 style={{ fontSize: "1.5rem" }}>{isEditing ? form.title || "Edit Post" : "Create Post"}</h2>
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button onClick={() => navigate("/admin/posts")} className="btn btn-ghost" disabled={saving}>Cancel</button>
          <button onClick={() => handleSave("draft")} className="btn btn-ghost" disabled={saving}>{saving ? "Saving..." : "Save Draft"}</button>
          <button onClick={() => handleSave("published")} className="btn btn-gold" disabled={saving}>{saving ? "Publishing..." : isEditing ? "Update & Publish" : "Publish"}</button>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 320px", gap: 24 }}>

        {/* ── Main column ── */}
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

          {/* Title */}
          <div style={{ background: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: "var(--radius)", padding: 24 }}>
            <div className="form-group">
              <label className="form-label">Post Title *</label>
              <input
                type="text"
                value={form.title}
                onChange={handleTitleChange}
                placeholder="Enter a compelling, descriptive title..."
                className="form-input"
                style={{ fontSize: "18px", padding: "14px 16px" }}
              />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">Slug (URL)</label>
              <input
                type="text"
                value={form.slug}
                onChange={e => set("slug", e.target.value)}
                placeholder="post-url-slug"
                className="form-input"
                style={{ fontFamily: "var(--font-mono)", fontSize: "13px" }}
              />
            </div>
          </div>

          {/* Excerpt */}
          <div style={{ background: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: "var(--radius)", padding: 24 }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">Excerpt / Summary</label>
              <textarea
                value={form.excerpt}
                onChange={e => set("excerpt", e.target.value)}
                placeholder="A brief, clear summary of this post (used in cards and meta descriptions)..."
                className="form-textarea"
                rows={3}
              />
            </div>
          </div>

          {/* ── Content ── */}
          <div style={{ background: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: "var(--radius)", padding: 24 }}>

            {/* Content header row */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <label className="form-label" style={{ margin: 0 }}>Report Content</label>
              <div style={{ display: "flex", gap: 8 }}>
                {/* Insert Template — only shown when a template exists for this category */}
                {contentConfig.template && !form.content.trim() && (
                  <button
                    type="button"
                    onClick={() => set("content", contentConfig.template)}
                    style={{
                      fontFamily: "var(--font-mono)",
                      fontSize: "10px",
                      letterSpacing: "0.12em",
                      textTransform: "uppercase",
                      padding: "5px 14px",
                      borderRadius: "20px",
                      border: `1px solid ${accentColor}50`,
                      background: accentColor + "15",
                      color: accentColor,
                      cursor: "pointer",
                      transition: "all 0.2s",
                    }}
                  >
                    ✦ Insert Template
                  </button>
                )}
                {/* Preview toggle */}
                <button
                  type="button"
                  onClick={() => setShowPreview(p => !p)}
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontSize: "10px",
                    letterSpacing: "0.14em",
                    textTransform: "uppercase",
                    padding: "5px 14px",
                    borderRadius: "20px",
                    border: `1px solid ${showPreview ? accentColor + "60" : "var(--border)"}`,
                    background: showPreview ? accentColor + "12" : "transparent",
                    color: showPreview ? accentColor : "var(--text-muted)",
                    cursor: "pointer",
                    transition: "all 0.2s",
                  }}
                >
                  {showPreview ? "✦ Preview On" : "Preview Off"}
                </button>
              </div>
            </div>

            {/* Textarea */}
            {!showPreview && (
              <div className="form-group" style={{ marginBottom: 0 }}>
                <textarea
                  value={form.content}
                  onChange={e => set("content", e.target.value)}
                  placeholder={contentConfig.placeholder}
                  className="form-textarea"
                  rows={22}
                  style={{ fontFamily: "var(--font-mono)", fontSize: "13px", lineHeight: 1.7 }}
                />
              </div>
            )}

            {/* Live preview panel */}
            {showPreview && (
              <div style={{
                minHeight: 400,
                background: "#050505",
                border: "1px solid #1e1e1e",
                borderRadius: "var(--radius)",
                padding: "32px 36px",
                overflowY: "auto",
              }}>
                <style>{`
                  .preview-wrap p {
                    font-family: Georgia, serif;
                    font-size: 17px;
                    line-height: 2;
                    color: #c8c8c8;
                    margin-bottom: 1.8em;
                    letter-spacing: 0.01em;
                  }
                  .preview-wrap > .article-content > p:first-of-type::first-letter {
                    font-size: 3.8rem;
                    font-weight: 700;
                    float: left;
                    line-height: 0.78;
                    margin: 6px 12px 0 0;
                    color: ${accentColor};
                    text-shadow: 0 0 24px ${accentColor}60;
                  }
                  .preview-wrap h2 {
                    font-family: 'Barlow', sans-serif;
                    font-weight: 700;
                    font-size: 1.2rem;
                    text-transform: uppercase;
                    letter-spacing: 0.06em;
                    color: #f0f0f0;
                    margin: 2.5em 0 0.9em;
                    padding-bottom: 10px;
                    border-bottom: 1px solid #1e1e1e;
                    position: relative;
                  }
                  .preview-wrap h2::after {
                    content: '';
                    position: absolute; bottom: -1px; left: 0;
                    width: 40px; height: 2px;
                    background: ${accentColor};
                  }
                `}</style>
                <div className="preview-wrap">
                  {form.content.trim() ? (
                    <FormattedReportContent content={form.content} accentColor={accentColor} />
                  ) : (
                    <p style={{ color: "#333", fontStyle: "italic", fontSize: "14px", fontFamily: "var(--font-mono)" }}>
                      Start typing in the editor to see a live preview here…
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Helper hint — only shown when the category has known headings */}
            {contentConfig.headings.length > 0 && (
              <div style={{ marginTop: 14, padding: "14px 16px", background: "#0a0a0a", border: "1px solid #1a1a1a", borderRadius: 8 }}>
                <p style={{ fontFamily: "var(--font-mono)", fontSize: "10px", color: "var(--text-muted)", marginBottom: 8, letterSpacing: "0.1em", textTransform: "uppercase" }}>
                  ✦ Auto-detected section headings for {form.category.replace(/-/g, " ")} — paste these exactly:
                </p>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 12px" }}>
                  {contentConfig.headings.map(h => (
                    <span
                      key={h}
                      style={{
                        fontFamily: "var(--font-mono)",
                        fontSize: "10px",
                        color: accentColor,
                        background: accentColor + "10",
                        border: `1px solid ${accentColor}25`,
                        borderRadius: "4px",
                        padding: "3px 8px",
                        letterSpacing: "0.05em",
                      }}
                    >
                      {h}
                    </span>
                  ))}
                </div>
                <p style={{ marginTop: 10, fontFamily: "var(--font-mono)", fontSize: "10px", color: "#333", letterSpacing: "0.06em" }}>
                  Separate paragraphs with a blank line. HTML tags are still supported if needed.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* ── Sidebar ── */}
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

          {/* Publishing */}
          <div style={{ background: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: "var(--radius)", padding: 20 }}>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: "10px", letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--text-muted)", marginBottom: 16 }}>Publishing</div>
            <div className="form-group">
              <label className="form-label">Status</label>
              <select value={form.status} onChange={e => set("status", e.target.value)} className="form-select">
                <option value="draft">Draft</option>
                <option value="published">Published</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Publish Date</label>
              <input type="date" value={form.publishDate} onChange={e => set("publishDate", e.target.value)} className="form-input" style={{ colorScheme: "dark" }} />
            </div>
            <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
              <input type="checkbox" checked={form.featured} onChange={e => set("featured", e.target.checked)} style={{ accentColor: "var(--gold)", width: 16, height: 16 }} />
              <span style={{ fontSize: "14px", color: "var(--text-secondary)" }}>Mark as Featured</span>
            </label>
          </div>

          {/* Category */}
          <div style={{ background: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: "var(--radius)", padding: 20 }}>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: "10px", letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--text-muted)", marginBottom: 16 }}>Category</div>
            <select value={form.category} onChange={e => set("category", e.target.value)} className="form-select">
              {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>

          {/* Tags */}
          <div style={{ background: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: "var(--radius)", padding: 20 }}>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: "10px", letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--text-muted)", marginBottom: 16 }}>Tags</div>
            <input type="text" value={form.tags} onChange={e => set("tags", e.target.value)} placeholder="gold, XAUUSD, macro, ..." className="form-input" style={{ fontSize: "13px" }} />
            <p style={{ marginTop: 6, fontSize: "11px", color: "var(--text-muted)", fontFamily: "var(--font-mono)" }}>Comma-separated</p>
          </div>

          {/* Author */}
          <div style={{ background: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: "var(--radius)", padding: 20 }}>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: "10px", letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--text-muted)", marginBottom: 16 }}>Author</div>
            <input type="text" value={form.author} onChange={e => set("author", e.target.value)} placeholder="MacroEdge Research" className="form-input" style={{ fontSize: "13px" }} />
          </div>

          {/* Cover Image */}
          <div style={{ background: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: "var(--radius)", padding: 20 }}>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: "10px", letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--text-muted)", marginBottom: 16 }}>Cover Image</div>
            {form.coverImage && (
              <div style={{ marginBottom: 12 }}>
                <img src={form.coverImage} alt="Cover" style={{ width: "100%", height: 120, objectFit: "cover", borderRadius: "var(--radius)", border: "1px solid var(--border)" }} />
                <button onClick={() => set("coverImage", "")} style={{ marginTop: 8, fontFamily: "var(--font-mono)", fontSize: "10px", color: "#e05252", background: "none", border: "none", cursor: "pointer" }}>✕ Remove</button>
              </div>
            )}
            <input type="file" ref={fileRef} accept="image/*" style={{ display: "none" }} onChange={handleImageUpload} />
            <button onClick={() => fileRef.current?.click()} className="btn btn-ghost w-full" style={{ justifyContent: "center", fontSize: "12px", width: "100%" }} disabled={imageUploading > 0}>
              {imageUploading > 0 ? `Uploading ${imageUploading}%...` : "Upload Image"}
            </button>
            <div className="form-group" style={{ marginTop: 12, marginBottom: 0 }}>
              <label className="form-label">Or paste image URL</label>
              <input type="url" value={form.coverImage} onChange={e => set("coverImage", e.target.value)} placeholder="https://..." className="form-input" style={{ fontSize: "12px" }} />
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
